import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import KioskMode from "@/pages/KioskMode";
import { isPatient, isDirectivo } from "@/lib/api";

// Páginas pesadas (Recharts, TipTap, PDF, tablas grandes) se cargan bajo demanda con Suspense.
// Esto evita colisiones de montaje/desmontaje durante navegación (removeChild) al reducir
// el árbol activo en cada transición y liberar chunks al cambiar de sección.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CalendarPage = lazy(() => import("@/pages/Calendar"));
const Patients = lazy(() => import("@/pages/Patients"));
const PatientDetail = lazy(() => import("@/pages/PatientDetail"));
const Professionals = lazy(() => import("@/pages/Professionals"));
const Billing = lazy(() => import("@/pages/Billing"));
const Tariffs = lazy(() => import("@/pages/Tariffs"));
const Statistics = lazy(() => import("@/pages/Statistics"));
const PatientPortal = lazy(() => import("@/pages/PatientPortal"));
const Absences = lazy(() => import("@/pages/Absences"));
const Coordinacion = lazy(() => import("@/pages/Coordinacion"));
const Asistencias = lazy(() => import("@/pages/Asistencias"));
const Messaging = lazy(() => import("@/pages/Messaging"));
const Timeclock = lazy(() => import("@/pages/Timeclock"));
const VacationCalendar = lazy(() => import("@/pages/VacationCalendar"));
const GlobalVacations = lazy(() => import("@/pages/GlobalVacations"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const ExpensesArchive = lazy(() => import("@/pages/ExpensesArchive"));
const BillingSettings = lazy(() => import("@/pages/BillingSettings"));
const LegalDocumentsManager = lazy(() => import("@/pages/LegalDocumentsManager"));
const MisTareas = lazy(() => import("@/pages/MisTareas"));
const MyInvoices = lazy(() => import("@/pages/MyInvoices"));
const MyProfessionalCard = lazy(() => import("@/pages/MyProfessionalCard"));
const EntidadesColaboradoras = lazy(() => import("@/pages/EntidadesColaboradoras"));
const PatientGroups = lazy(() => import("@/pages/PatientGroups"));
const EntityDirectorPanel = lazy(() => import("@/pages/EntityDirectorPanel"));

function LoadingFallback() {
    return (
        <div className="flex items-center justify-center h-[60vh]" data-testid="page-loading">
            <div className="text-muted-foreground text-sm">Cargando…</div>
        </div>
    );
}

function Protected({ children }) {
    const { user } = useAuth();
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    if (user === null) return <div className="p-10 text-muted-foreground">Cargando…</div>;
    if (!user) return <Navigate to="/login" replace />;
    // Cambio de contraseña obligatorio en primer acceso (paciente creado por staff)
    if (user.must_change_password && path !== "/change-password") {
        return <Navigate to="/change-password" replace />;
    }
    return children;
}

function HomeRedirect() {
    const { user } = useAuth();
    if (isPatient(user)) return <Navigate to="/mi-cuenta" replace />;
    if (user?.role === "directora_entidad") return <Navigate to="/entidad" replace />;
    // Coordinadora tiene su panel propio de coordinación
    if (user?.role === "coordinadora") return <Navigate to="/coordinacion" replace />;
    // Profesionales (internos y externos) arrancan en su agenda
    if (!isDirectivo(user)) return <Navigate to="/calendario" replace />;
    return (
        <Suspense fallback={<LoadingFallback />}>
            <Dashboard />
        </Suspense>
    );
}

function LazyRoute({ Component }) {
    return (
        <Suspense fallback={<LoadingFallback />}>
            <Component />
        </Suspense>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="top-right" />
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/quiosco" element={<KioskMode />} />
                    <Route path="/change-password" element={
                        <Protected>
                            <Suspense fallback={<LoadingFallback />}>
                                <ChangePassword />
                            </Suspense>
                        </Protected>
                    } />
                    <Route path="/" element={<Protected><Layout /></Protected>}>
                        <Route index element={<HomeRedirect />} />
                        <Route path="entidad" element={<LazyRoute Component={EntityDirectorPanel} />} />
                        <Route path="calendario" element={<LazyRoute Component={CalendarPage} />} />
                        <Route path="pacientes" element={<LazyRoute Component={Patients} />} />
                        <Route path="pacientes/entidades" element={<LazyRoute Component={EntidadesColaboradoras} />} />
                        <Route path="pacientes/grupos" element={<LazyRoute Component={PatientGroups} />} />
                        <Route path="pacientes/:id" element={<LazyRoute Component={PatientDetail} />} />
                        <Route path="coordinacion" element={<LazyRoute Component={Coordinacion} />} />
                        <Route path="coordinacion/:section" element={<LazyRoute Component={Coordinacion} />} />
                        <Route path="asistencias" element={<LazyRoute Component={Asistencias} />} />
                        <Route path="mensajeria" element={<LazyRoute Component={Messaging} />} />
                        <Route path="fichaje" element={<LazyRoute Component={Timeclock} />} />
                        <Route path="profesionales" element={<LazyRoute Component={Professionals} />} />
                        <Route path="facturacion" element={<LazyRoute Component={Billing} />} />
                        <Route path="facturacion/configuracion" element={<LazyRoute Component={BillingSettings} />} />
                        <Route path="tarifas" element={<LazyRoute Component={Tariffs} />} />
                        <Route path="estadisticas" element={<LazyRoute Component={Statistics} />} />
                        <Route path="mi-cuenta" element={<LazyRoute Component={PatientPortal} />} />
                        <Route path="ausencias" element={<LazyRoute Component={Absences} />} />
                        <Route path="mis-tareas" element={<LazyRoute Component={MisTareas} />} />
                        <Route path="archivo-gastos" element={<LazyRoute Component={ExpensesArchive} />} />
                        <Route path="vacaciones" element={<LazyRoute Component={VacationCalendar} />} />
                        <Route path="vacaciones-global" element={<LazyRoute Component={GlobalVacations} />} />
                        <Route path="documentos-legales" element={<LazyRoute Component={LegalDocumentsManager} />} />
                        <Route path="mis-facturas" element={<LazyRoute Component={MyInvoices} />} />
                        <Route path="mi-ficha" element={<LazyRoute Component={MyProfessionalCard} />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
